import { useState } from "react";
import Image from "next/image";
import classNames from "classnames";
import dynamic from "next/dynamic";
import { PiCubeTransparent } from "react-icons/pi";
import { HiOutlineMap } from "react-icons/hi";

// components
import Button from "@/shared/Button";
const LocationSpaceImagesModal = dynamic(() => import("@/components/Modals/LocationSpaceImagesModal"), { ssr: false });
const LocationSpaceVideoModal = dynamic(() => import("@/components/Modals/LocationSpaceVideoModal"), { ssr: false });

// helpers
import { handleVirtualTourClick } from "@/helpers/handleVirtualTourClick";

// interface
import { ILocationSpaceImages, ILocationSpaceImage } from "./ILocationSpaceImages";

//styles
import styles from "@/assets/styles/components/location-space-images.module.scss";

const LocationSpaceImages = (props: ILocationSpaceImages) => {
  const {
    images,
    title,
    virtualTourUrl,
    isWithFloorPlan,
    onFloorPlanClick,
    isRestricted,
    type = "space",
    onRestrictedButtonClick,
    onVirtualTourClick,
  } = props;

  const [modalVideo, setModalVideo] = useState<string | null>(null);
  const [isAllImageModalOpen, setIsAllImageModalOpen] = useState<boolean>(false);

  // methods
  const handleImageClick = (item: ILocationSpaceImage) => {
    if (item.videoHtml) {
      setModalVideo(item.videoHtml as string);

      return;
    }

    setIsAllImageModalOpen(true)
  }

  const handleFloorPlanButton = (e: React.MouseEvent<HTMLElement>) => {
    if (isRestricted && type === "location") {
      onRestrictedButtonClick && onRestrictedButtonClick(e);

      return;
    }

    onFloorPlanClick && onFloorPlanClick();
  }

  const handleVirtualTourButton = (e: React.MouseEvent<HTMLElement>) => {
    if (isRestricted) {
      onRestrictedButtonClick && onRestrictedButtonClick(e);

      return;
    }

    handleVirtualTourClick(e, virtualTourUrl || "");
    onVirtualTourClick && onVirtualTourClick();
  }

  // renders
  const renderImage = (item: ILocationSpaceImage, isLazyLoading?: boolean) => (
    <div className={styles["locl-location-space-images-block"]}>
      <Image
        src={item.src}
        alt={item.alt || "location image"}
        width={830}
        height={518}
        quality={100}
        loading={isLazyLoading ? "lazy" : "eager"}
        priority={!isLazyLoading}
        onClick={() => handleImageClick(item)}
        className={styles["locl-location-space-images-block__image"]}
      />
    </div>
  )

  if (!images.length) {
    return null;
  }

  return (<>
    <div className={classNames(
      styles["locl-location-space-images"],
      { [styles["locl-location-space-images__many-images"]]: images.length >= 3 },
    )}>
      { renderImage(images[0] as ILocationSpaceImage) }
      { images.map((image, index) => index > 0 && index <= 2 && renderImage(image, true)) }
      <div className={styles["locl-location-space-images__actions"]}>
        <Button
          type="secondary"
          onClick={() => setIsAllImageModalOpen(true)}
          className={styles["locl-location-space-images__all-images-button"]}
          data-testid="locl-location-space-images-all-images-button"
        >
          All photos
        </Button>
        {
          virtualTourUrl && (
            <Button
              type="secondary"
              href={virtualTourUrl}
              className={styles["locl-location-space-images__virtual-tour-button"]}
              onClick={handleVirtualTourButton}
              iconRight={<PiCubeTransparent className={styles["locl-location-space-images__actions__button-icon"]} />}
              data-testid="locl-location-space-images-virtual-tour-button"
            >
              Virtual tour
            </Button>
          )
        }
        {
          isWithFloorPlan && (
            <Button
              type="secondary"
              className={styles["locl-location-space-images__floor-plan-button"]}
              iconRight={<HiOutlineMap className={styles["locl-location-space-images__actions__button-icon"]} />}
              onClick={handleFloorPlanButton}
              data-testid="locl-location-space-images-floor-plan-button"
            >
              Floor plan
            </Button>
          )
        }
      </div>
    </div>
    <LocationSpaceImagesModal
      open={isAllImageModalOpen}
      onCancel={() => setIsAllImageModalOpen(false)}
      title={title}
      images={images}
      onImageClick={setModalVideo}
    />
    <LocationSpaceVideoModal
      open={!!modalVideo}
      modalVideo={modalVideo as string}
      onCancel={() => setModalVideo(null)}
    />
  </>)
}

export default LocationSpaceImages;
